import { Time } from './Time'

export enum DateComponentUnit {
    DAY_OF_WEEK = 'DAY_OF_WEEK',
    DAY_OF_MONTH = 'DAY_OF_MONTH',
    DAY_OF_YEAR = 'DAY_OF_YEAR',
    MONTH_OF_YEAR = 'MONTH_OF_YEAR'
}

export class DateComponent {
    constructor(
        public unit: DateComponentUnit,
        public value: number
    ) { }
}

export enum MonthlyRepetitionType {
    DayOfMonth,
    DayOfWeek,
}

export class Event {

    constructor(
        public id: string,
        public displayName: string,
        public displayOrder: number,
        public description: string,
        public imageId: string | null,
        public cancellationChargeAmount: number | null,
        public cancellationChargePartyAmount: number | null,
        public cancellationChargeCutOffHours: number | null,
        public depositAmount: number | null,
        public depositFlatFeeAmount: number | null,
        public depositRefundCutOffDays: number | null,
        public isExclusive: boolean,
        public questionIds: string[],
        public notReservableOnline: boolean,
        public notReservableOnlineMessage: string | null,
        public recurrence: EventRecurrence,
        public partySizeRestriction: number[] | null,
        public partySizeRestrictedMessage: string | null,
        public dogFriendlyNotAllowed: boolean,
        public minLargePartySize: number | null,
        public tableIds: string[]
    ) { }

    isPartySizeAllowed(partySize: number): boolean {
        if (this.partySizeRestriction === null) {
            return true
        }
        return this.partySizeRestriction.includes(partySize)
    }
}

export class EventRecurrence {

    constructor(
        public startDates: Date[],
        public dateComponent: DateComponent | null,
        public weeklyRepeatsOnDaysOfWeek: number[] | null,
        public monthlyRepetitionType: MonthlyRepetitionType | null,
        public exceptions: EventException[],
        public endDate: Date | null,
        public startTime: Time | null,
        public endTime: Time | null
    ) { }

    appliesToDate(dateTime: Date): boolean {
        if (this.startDates.length === 0) {
            return false
        }
        const date = this.startOfDay(dateTime)
        const startDate = this.startOfDay(this.earliestStartDate())
        if (date < startDate) {
            return false
        }
        if (this.endDate && date > this.startOfDay(this.endDate)) {
            return false
        }
        if (this.hasExceptionOnDate(date)) {
            return false
        }
        const matchesStartDate = this.startDates.some(startDate => this.isSameDay(date, startDate))
        if (!this.dateComponent) {
            return matchesStartDate
        }
        if (matchesStartDate && !this.isSameDay(date, startDate)) {
            return true
        }
        if (this.dateComponent.unit === DateComponentUnit.DAY_OF_WEEK) {
            if (!this.weeklyRepeatsOnDaysOfWeek) {
                return false
            }
            if (!this.weeklyRepeatsOnDaysOfWeek.map(day => this.javaDayOfWeekToJsDay(day)).includes(date.getDay())) {
                return false
            }
            if (!this.doesDateOccurInWeeklySchedule(date, this.dateComponent)) {
                return false
            }
        }
        if (this.dateComponent.unit === DateComponentUnit.DAY_OF_MONTH) {
            if (this.monthlyRepetitionType === null) {
                return false
            }
            if (this.monthlyRepetitionType === MonthlyRepetitionType.DayOfMonth) {
                if (date.getDate() !== startDate.getDate()) {
                    return false
                }
            }
            if (this.monthlyRepetitionType === MonthlyRepetitionType.DayOfWeek) {
                if (date.getDay() !== startDate.getDay()) {
                    return false
                }
                const scheduledOrdinalWeekOfMonth = this.getScheduledOrdinalWeekOfMonth(date)
                const dateOrdinalWeekOfMonth = this.alignedWeekOfMonth(date)
                if (scheduledOrdinalWeekOfMonth !== dateOrdinalWeekOfMonth) {
                    return false
                }
            }
            if (!this.doesDateOccurInMonthlySchedule(date, this.dateComponent)) {
                return false
            }
        }
        return true
    }

    nextDateTimeOnOrAfter(referenceDate: Date): Date | null {
        return this.upcomingDateTimes(referenceDate, 1)[0] ?? null
    }

    upcomingDateTimes(referenceDate: Date, limit: number): Date[] {
        if (this.startDates.length === 0 || limit <= 0) {
            return []
        }
        if (this.dateComponent === null) {
            const dateTimes = this.startDates
                .filter(date => this.appliesToDate(date))
                .map(date => this.dateTimeOnDate(date))
                .filter(dateTime => this.occurrenceCanStillBeBooked(dateTime, referenceDate))
                .sort((a, b) => a.getTime() - b.getTime())
            return this.uniqueDateTimesByDate(dateTimes).slice(0, limit)
        }
        const dates: Date[] = []
        const searchStartDate = this.startOfDay(referenceDate)
        const searchEndDate = this.searchEndDate(searchStartDate, limit)
        let currentDate = searchStartDate
        while (currentDate <= searchEndDate && dates.length < limit) {
            if (this.appliesToDate(currentDate)) {
                const dateTime = this.dateTimeOnDate(currentDate)
                if (this.occurrenceCanStillBeBooked(dateTime, referenceDate)) {
                    dates.push(dateTime)
                }
            }
            currentDate = this.plusDays(currentDate, 1)
        }
        return dates
    }

    private uniqueDateTimesByDate(dateTimes: Date[]): Date[] {
        return dateTimes.filter((dateTime, index, allDateTimes) => {
            return allDateTimes.findIndex(otherDateTime => {
                return this.isSameDay(otherDateTime, dateTime)
            }) === index
        })
    }

    earliestStartDate(): Date {
        return this.startDates.reduce((earliest, current) => {
            return current < earliest ? current : earliest
        })
    }

    private occurrenceCanStillBeBooked(dateTime: Date, referenceDate: Date): boolean {
        if (this.hasKnownStartTime(dateTime)) {
            return dateTime >= referenceDate
        }
        return this.endOfDay(dateTime) >= referenceDate
    }

    private hasKnownStartTime(dateTime: Date): boolean {
        return this.startTime !== null || !this.isStartOfDay(dateTime)
    }

    private dateTimeOnDate(date: Date): Date {
        if (this.startTime) {
            return this.startTime.dateTimeOnDate(date)
        }
        const matchingStartDate = this.startDates
            .filter(startDate => this.isSameDay(startDate, date))
            .sort((a, b) => a.getTime() - b.getTime())[0]
        const timeSource = matchingStartDate ?? this.earliestStartDate()
        const dateTime = new Date(date)
        dateTime.setHours(
            timeSource.getHours(),
            timeSource.getMinutes(),
            timeSource.getSeconds(),
            timeSource.getMilliseconds()
        )
        return dateTime
    }

    private searchEndDate(startDate: Date, limit: number): Date {
        if (this.endDate !== null) {
            return this.startOfDay(this.endDate)
        }
        return this.plusDays(startDate, this.maxDaysToSearch(limit))
    }

    private maxDaysToSearch(limit: number): number {
        const repeatValue = Math.max(1, this.dateComponent?.value ?? 1)
        if (this.dateComponent?.unit === DateComponentUnit.DAY_OF_WEEK) {
            return (Math.max(7, repeatValue * 7) * limit) + 7
        }
        if (this.dateComponent?.unit === DateComponentUnit.DAY_OF_MONTH) {
            return (Math.max(31, repeatValue * 31) * limit) + 31
        }
        return 366 * limit
    }

    private hasExceptionOnDate(date: Date): boolean {
        return this.exceptions.some(exception => this.isSameDay(exception.date, date))
    }

    private getScheduledOrdinalWeekOfMonth(date: Date): number {
        const startDate = this.startOfDay(this.earliestStartDate())
        const ordinalStartDateWeekOfMonth = this.alignedWeekOfMonth(startDate)
        const largestWeekOfMonthOnWeekday = this.alignedWeekOfMonth(
            this.lastMatchingDayOfMonth(date, startDate)
        )
        if (ordinalStartDateWeekOfMonth > largestWeekOfMonthOnWeekday) {
            return largestWeekOfMonthOnWeekday
        }
        return ordinalStartDateWeekOfMonth
    }

    private lastMatchingDayOfMonth(referenceDate: Date, matchDayDate: Date): Date {
        const lastDay = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0)
        while (lastDay.getDay() !== matchDayDate.getDay()) {
            lastDay.setDate(lastDay.getDate() - 1)
        }
        return lastDay
    }

    private alignedWeekOfMonth(date: Date): number {
        return Math.floor((date.getDate() - 1) / 7) + 1
    }

    private doesDateOccurInWeeklySchedule(date: Date, dateComponent: DateComponent): boolean {
        const repeatValue = Math.max(1, dateComponent.value)
        const startDate = this.startOfDay(this.earliestStartDate())
        const startOfStartDateWeek = this.startOfWeekOnMonday(startDate)
        const startOfDateWeek = this.startOfWeekOnMonday(date)
        const weeksBetween = Math.floor(
            (startOfDateWeek.getTime() - startOfStartDateWeek.getTime()) / (7 * 24 * 60 * 60 * 1000)
        )
        return weeksBetween % repeatValue === 0
    }

    private doesDateOccurInMonthlySchedule(date: Date, dateComponent: DateComponent): boolean {
        const repeatValue = Math.max(1, dateComponent.value)
        const startDate = this.startOfDay(this.earliestStartDate())
        const monthsBetween = (date.getFullYear() - startDate.getFullYear()) * 12
            + date.getMonth()
            - startDate.getMonth()
        return monthsBetween % repeatValue === 0
    }

    private javaDayOfWeekToJsDay(dayOfWeek: number): number {
        return dayOfWeek === 7 ? 0 : dayOfWeek
    }

    private isSameDay(first: Date, second: Date): boolean {
        return first.getFullYear() === second.getFullYear()
            && first.getMonth() === second.getMonth()
            && first.getDate() === second.getDate()
    }

    private isStartOfDay(date: Date): boolean {
        return date.getHours() === 0
            && date.getMinutes() === 0
            && date.getSeconds() === 0
            && date.getMilliseconds() === 0
    }

    private startOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }

    private endOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0, -1)
    }

    private startOfWeekOnMonday(date: Date): Date {
        const startOfDay = this.startOfDay(date)
        const day = startOfDay.getDay()
        const daysFromMonday = day === 0 ? 6 : day - 1
        return this.plusDays(startOfDay, -daysFromMonday)
    }

    private plusDays(date: Date, days: number): Date {
        const nextDate = new Date(date)
        nextDate.setDate(nextDate.getDate() + days)
        return nextDate
    }
}

export class EventException {

    constructor(
        public id: string,
        public date: Date
    ) { }
}
